import type { KnowledgeSelection } from '@pcp/shared';

/**
 * Multi-select of which knowledge sources should ground an AI conversation.
 * Any number can be selected; the choice overrides the global Knowledge
 * toggles for that conversation. Used by NewChatModal and DiscussModal.
 */
interface KnowledgeSelectorProps {
  value: KnowledgeSelection;
  onChange: (next: KnowledgeSelection) => void;
}

type Key = keyof KnowledgeSelection;

const GROUPS: { group: string; items: { key: Key; label: string }[] }[] = [
  {
    group: 'Work items',
    items: [
      { key: 'features', label: 'Features' },
      { key: 'userStories', label: 'User Stories' },
      { key: 'bugs', label: 'Bugs' },
    ],
  },
  {
    group: 'Discussions',
    items: [
      { key: 'topics', label: 'Discourse topics' },
      { key: 'tickets', label: 'Zoho tickets' },
    ],
  },
  {
    group: 'Code',
    items: [{ key: 'commits', label: 'Commits' }],
  },
  {
    group: 'Documents',
    items: [
      { key: 'userDocs', label: 'User documents' },
      { key: 'systemDocs', label: 'System documents' },
    ],
  },
  {
    group: 'Configuration',
    items: [{ key: 'cocoConfig', label: 'CoCo config' }],
  },
];

const ALL_KEYS: Key[] = GROUPS.flatMap((g) => g.items.map((i) => i.key));

export function knowledgeSelectionCount(sel: KnowledgeSelection | null | undefined): number {
  if (!sel) return 0;
  return ALL_KEYS.reduce((n, k) => n + (sel[k] ? 1 : 0), 0);
}

export function KnowledgeSelector({ value, onChange }: KnowledgeSelectorProps) {
  const toggle = (key: Key, on: boolean) => onChange({ ...value, [key]: on });
  const setAll = (on: boolean) => {
    const next: KnowledgeSelection = {};
    if (on) for (const k of ALL_KEYS) next[k] = true;
    onChange(next);
  };
  const count = knowledgeSelectionCount(value);

  const chip: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 6, fontSize: '13px', color: 'var(--text-primary)',
    padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <label style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-secondary)' }}>
          Knowledge to include{count > 0 ? ` (${count})` : ''}
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={() => setAll(true)} style={{ fontSize: '11px', color: 'var(--accent-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>All</button>
          <button type="button" onClick={() => setAll(false)} style={{ fontSize: '11px', color: 'var(--text-secondary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>None</button>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        {GROUPS.map((g) => (
          <div key={g.group} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{g.group}</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {g.items.map((it) => (
                <label key={it.key} style={{ ...chip, borderColor: value[it.key] ? 'var(--accent-primary)' : 'var(--border)', background: value[it.key] ? 'var(--accent-primary-soft)' : 'var(--bg-card)' }}>
                  <input type="checkbox" checked={!!value[it.key]} onChange={(e) => toggle(it.key, e.target.checked)} />
                  {it.label}
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
      <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
        Selected sources are retrieved by similarity and cited in replies — even if disabled in global Knowledge settings. Pick none for a plain chat.
      </span>
    </div>
  );
}
